/**
 * /hospitality/title-31 — Title 31 / BSA cash-transaction watch.
 *
 * Mirrors a casino's Title 31 compliance day: which patrons crossed the
 * $10k CTR threshold in cash in / cash out (must be filed), and which
 * cash-transaction patterns look like structuring (SAR candidates) — the
 * latter caught by pattern even when no CTR was triggered.
 *
 * All money fields arrive as decimal strings; `money()` formats them.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useHospitalityPack } from "@/lib/useHospitalityPack";
import { api } from "@/lib/api";
import { PageHeader, StatCard, Panel, StatusPill, money } from "@/components/hospitality/ui";

export const Route = createFileRoute("/_auth/hospitality/title-31")({
  component: Title31Page,
});

// ── Types — matches GET /api/hospitality/title-31 contract ─────────────────
interface Title31Ctr {
  patron_ref: string;
  patron_name: string;
  direction: "cash_in" | "cash_out";
  aggregate: string;
  transaction_count: number;
  areas: string[];
  note: string;
}
interface Title31Sar {
  patron_ref: string;
  patron_name: string;
  cash_in: string;
  cash_out: string;
  indicators: string[];
  ctr_also_filed: boolean;
  detail: string;
}
interface Title31Result {
  property: string;
  gaming_date: string | null;
  patrons_screened: number;
  transactions_count: number;
  ctrs: Title31Ctr[];
  sars: Title31Sar[];
  clean_count: number;
  totals: { cash_in: string; cash_out: string };
  message?: string;
}

// ── Formatters ─────────────────────────────────────────────────────────────
const DIRECTION_LABEL: Record<Title31Ctr["direction"], string> = {
  cash_in: "Cash in",
  cash_out: "Cash out",
};

const INDICATOR_LABEL: Record<string, string> = {
  near_threshold: "Near threshold",
  minimal_play_round_trip: "Minimal-play round trip",
  repeated_just_under: "Repeated just-under",
};
function indicatorLabel(code: string): string {
  return INDICATOR_LABEL[code] ?? code;
}

function Title31Page() {
  const { enabled } = useHospitalityPack();
  if (!enabled) return null;

  const q = useQuery({
    queryKey: ["hospitality-title-31"],
    queryFn: () => api<Title31Result>("/api/hospitality/title-31"),
  });

  const data = q.data;

  return (
    <div className="flex flex-col h-[calc(100vh-56px)] overflow-hidden">
      <PageHeader
        title="Title 31 — cash-transaction watch"
        subtitle={
          data
            ? `${data.property}${data.gaming_date ? ` · ${data.gaming_date}` : ""}`
            : "CTR + structuring (SAR) cash-transaction watch"
        }
        right={
          data && !data.message ? (
            <StatusPill tone={data.ctrs.length > 0 ? "exception" : "success"}>
              {data.ctrs.length} CTR{data.ctrs.length === 1 ? "" : "s"}
            </StatusPill>
          ) : undefined
        }
      />

      <div className="flex-1 min-h-0 overflow-auto px-6 py-4">
        {q.isLoading && (
          <div className="text-sm text-slate-500 dark:text-slate-400">Loading…</div>
        )}

        {!q.isLoading && data?.message && (
          <div className="text-sm text-slate-500 py-12 text-center dark:text-slate-400">
            {data.message}
            <br />
            <span className="text-xs">
              Title 31 results populate once cash transactions are imported for a gaming date.
            </span>
          </div>
        )}

        {!q.isLoading && data && !data.message && (
          <div className="space-y-4">
            {/* Summary cards */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5">
              <StatCard
                label="CTRs required"
                value={String(data.ctrs.length)}
                tone={data.ctrs.length > 0 ? "danger" : "default"}
              />
              <StatCard
                label="SAR candidates"
                value={String(data.sars.length)}
                tone={data.sars.length > 0 ? "warning" : "default"}
              />
              <StatCard label="Patrons screened" value={String(data.patrons_screened)} />
              <StatCard
                label="Cash in / Cash out"
                value={`${money(data.totals.cash_in, 0)} / ${money(data.totals.cash_out, 0)}`}
              />
            </div>

            {/* CTRs required */}
            <Panel label="CTRs required">
              <table className="w-full text-sm">
                <thead className="border-b border-slate-100 dark:border-slate-800 text-[10px] uppercase tracking-wide text-slate-500 dark:text-slate-400">
                  <tr>
                    <th className="text-left py-1.5 pr-3">Patron</th>
                    <th className="text-left py-1.5 px-3">Direction</th>
                    <th className="text-right py-1.5 px-3">Aggregate</th>
                    <th className="text-right py-1.5 px-3"># Txns</th>
                    <th className="text-left py-1.5 pl-3">Areas</th>
                  </tr>
                </thead>
                <tbody>
                  {data.ctrs.length === 0 && (
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-slate-500 dark:text-slate-400">
                        No patrons crossed the CTR threshold for this gaming date.
                      </td>
                    </tr>
                  )}
                  {data.ctrs.map((c, i) => (
                    <tr
                      key={i}
                      className="border-t border-slate-100 dark:border-slate-800"
                      title={c.note}
                    >
                      <td className="py-1.5 pr-3">
                        <div className="flex items-center gap-2">
                          <StatusPill tone="exception">CTR</StatusPill>
                          <div className="min-w-0">
                            <div className="text-slate-800 dark:text-slate-200">{c.patron_name}</div>
                            <div className="text-xs text-slate-500 dark:text-slate-400">{c.patron_ref}</div>
                          </div>
                        </div>
                        {c.note && (
                          <div className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{c.note}</div>
                        )}
                      </td>
                      <td className="py-1.5 px-3 text-slate-600 dark:text-slate-300">
                        {DIRECTION_LABEL[c.direction]}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs font-medium text-slate-900 dark:text-slate-100">
                        {money(c.aggregate)}
                      </td>
                      <td className="py-1.5 px-3 text-right tabular-nums text-xs text-slate-600 dark:text-slate-300">
                        {c.transaction_count}
                      </td>
                      <td className="py-1.5 pl-3 text-slate-600 dark:text-slate-300">
                        {c.areas.join(", ")}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Panel>

            {/* SAR candidates */}
            <Panel label="SAR candidates">
              {data.sars.length === 0 ? (
                <div className="py-8 text-center text-sm text-slate-500 dark:text-slate-400">
                  No structuring patterns detected — nothing to refer for SAR review.
                </div>
              ) : (
                <ul>
                  {data.sars.map((s, i) => (
                    <li
                      key={i}
                      className="py-3 border-t border-slate-100 dark:border-slate-800 first:border-t-0"
                    >
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="text-sm font-semibold text-slate-800 dark:text-slate-200">
                          {s.patron_name}
                        </span>
                        <span className="text-xs text-slate-500 dark:text-slate-400">{s.patron_ref}</span>
                        <div className="flex-1" />
                        {!s.ctr_also_filed && (
                          <StatusPill tone="open">No CTR — caught by pattern only</StatusPill>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-slate-600 dark:text-slate-400">{s.detail}</div>
                      <div className="mt-1 text-xs tabular-nums text-slate-500 dark:text-slate-400">
                        In {money(s.cash_in)} · Out {money(s.cash_out)}
                      </div>
                      {s.indicators.length > 0 && (
                        <div className="mt-2 flex flex-wrap gap-1.5">
                          {s.indicators.map((ind) => (
                            <StatusPill key={ind} tone="neutral">
                              {indicatorLabel(ind)}
                            </StatusPill>
                          ))}
                        </div>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </Panel>
          </div>
        )}
      </div>
    </div>
  );
}
